import React, { useState, useEffect } from "react";
import { Box, useTheme, Typography, Button, Tabs, Tab, LinearProgress } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { getContratos } from "../../services/pedidoVenta.services";
import ModalHistorialEntregas from "../../modal/cargamento/modalHistorialEntregas";

const STATUS_LABELS = {
  PENDIENTE: "Pendiente",
  PARCIAL: "Parcial",
  COMPLETADO: "Completado",
};

const STATUS_COLORS = {
  PENDIENTE: "#f5c518",
  PARCIAL: "#00aaff",
  COMPLETADO: "#4caf50",
};

const Contratos = () => {
  const [data, setData] = useState([]);
  const [filtro, setFiltro] = useState("TODOS");
  const [historialOpen, setHistorialOpen] = useState(false);
  const [historialPedidoId, setHistorialPedidoId] = useState(null);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  useEffect(() => {
    getContratos()
      .then(setData)
      .catch((err) => console.error(err));
  }, []);

  const rows = data
    .filter((r) => filtro === "TODOS" || r.status_contrato === filtro)
    .map((r) => ({ ...r, id: r.id }));

  const columns = [
    { field: "n_pedido", headerName: "N° Pedido", width: 110 },
    { field: "cliente", headerName: "Cliente", flex: 1 },
    { field: "producto", headerName: "Producto", flex: 1.5 },
    {
      field: "cantidad_total",
      headerName: "Total",
      width: 90,
      type: "number",
    },
    {
      field: "cantidad_entregada",
      headerName: "Entregado",
      width: 100,
      type: "number",
    },
    {
      field: "cantidad_restante",
      headerName: "Saldo",
      width: 90,
      type: "number",
    },
    {
      field: "progreso",
      headerName: "Progreso",
      width: 160,
      renderCell: ({ row }) => {
        const pct = row.cantidad_total > 0
          ? Math.min(100, Math.round((row.cantidad_entregada / row.cantidad_total) * 100))
          : 0;
        return (
          <Box width="100%" display="flex" alignItems="center" gap="6px">
            <Box flex={1}>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: colors.primary[300],
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: STATUS_COLORS[row.status_contrato] ?? colors.blueAccent[400],
                  },
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ minWidth: 32 }}>{pct}%</Typography>
          </Box>
        );
      },
    },
    {
      field: "status_contrato",
      headerName: "Estado",
      width: 110,
      renderCell: ({ value }) => (
        <Box
          px={1}
          py={0.3}
          borderRadius={1}
          sx={{ backgroundColor: `${STATUS_COLORS[value] ?? "#888"}22`, color: STATUS_COLORS[value] ?? "#888" }}
        >
          <Typography variant="caption" fontWeight="bold">
            {STATUS_LABELS[value] ?? value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "created_at",
      headerName: "Fecha",
      width: 110,
      valueFormatter: ({ value }) => value ? new Date(value).toLocaleDateString() : "-",
    },
    {
      field: "acciones",
      headerName: "Historial",
      width: 110,
      sortable: false,
      renderCell: ({ row }) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => { setHistorialPedidoId(row.id); setHistorialOpen(true); }}
          sx={{ borderColor: colors.grey[400], color: colors.grey[200], fontSize: "0.7rem" }}
        >
          Ver viajes
        </Button>
      ),
    },
  ];

  const tabs = ["TODOS", "PENDIENTE", "PARCIAL", "COMPLETADO"];

  return (
    <Box m="20px">
      <Header title="Contratos" subtitle="Estado de contratos de venta" />

      <Tabs
        value={filtro}
        onChange={(_, v) => setFiltro(v)}
        textColor="secondary"
        indicatorColor="secondary"
        sx={{ mb: 2 }}
      >
        {tabs.map((t) => (
          <Tab key={t} label={t === "TODOS" ? "Todos" : STATUS_LABELS[t]} value={t} />
        ))}
      </Tabs>

      <Box
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          autoHeight
          disableSelectionOnClick
          rowHeight={52}
        />
      </Box>

      <ModalHistorialEntregas
        open={historialOpen}
        onClose={() => setHistorialOpen(false)}
        idPedidoVenta={historialPedidoId}
      />
    </Box>
  );
};

export default Contratos;
